import React, { useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import {
  ActionStrip,
  CheckboxInput,
  ConfirmationModal,
  Heading2,
} from '@acorns/web-components'
import { AnalyticsContextType, withAnalyticsClient } from '@acorns/web-utils'
import { useMutation, useQuery } from '@apollo/react-hooks'
import styled from 'styled-components'

import FullscreenTakeover from 'components/FullscreenTakeover'
import {
  InvestmentAccount,
  InvestmentAccountStatus,
  RequestEarlyAccountClosureInput,
} from 'generated/types'
import useWindowDimensions from 'hooks/useWindowDimensions'
import { EarlyAvatar } from 'pages/early/components/EarlyAvatar'
import { style } from 'theme'
import { USDFormatter } from 'utils/number/currency'
import { ModalActionTypes, PromptProps, withPrompt } from 'utils/prompts'

import { CloseAccountWrapper } from '../closeAccountWrapper'
import LoadingScreen from '../closureLoading'
import {
  StyledBody,
  StyledCard,
  StyledCloseAccountButton,
  StyledHeading,
} from '../components'
import earlyAccountClosureMutation from '../mutations/earlyAccountClosure.mutation.gql'
import earlyAccountsQuery from '../queries/earlyAccounts.query.gql'
import { routes } from '../routes'
import {
  EarlyCloseAccountsCTATapped,
  EarlyCloseConfirmationModal,
  EarlyCloseConfirmationModalConfirmCTA,
  EarlyCloseConfirmationModalNevermindCTA,
} from './segment'

type Props = AnalyticsContextType & PromptProps

type QueryType = {
  earlyAccountsData: {
    investmentAccountsByUserId: InvestmentAccount[]
  }
}

type MutationType = {
  reason: RequestEarlyAccountClosureInput['reason']
  investmentAccountsIds: RequestEarlyAccountClosureInput['investmentAccountIds']
}

const WVCard = styled(StyledCard)<{ innerHeight?: number }>`
  height: ${(props) => `calc(${props.innerHeight}px - 80px)`};
  &&& {
    border-radius: 0;
  }
`

const Card = styled(StyledCard)`
  padding: 30px 0px;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    &&& {
      padding: 20px 0px;
    }
  }
`

const StyledEarlyAvatar = styled(EarlyAvatar)`
  &&& {
    width: 43px;
    height: 43px;
    margin: 0;
    margin-right: 10px;
    margin-left: 10px;
  }
`

const WVItem = styled.div`
  border: 1px solid ${style((theme) => theme.colors.ivory)};
  &:first-child {
    border-top-left-radius: ${style((theme) => theme.borderRadius.large)};
    border-top-right-radius: ${style((theme) => theme.borderRadius.large)};
  }
  &:not(:first-child):not(:last-child) {
    border-top: none;
  }
  &:last-child:not(:first-child) {
    border-top: none;
    border-bottom-left-radius: ${style((theme) => theme.borderRadius.large)};
    border-bottom-right-radius: ${style((theme) => theme.borderRadius.large)};
  }
  &:last-child:first-child {
    border-bottom-left-radius: ${style((theme) => theme.borderRadius.large)};
    border-bottom-right-radius: ${style((theme) => theme.borderRadius.large)};
  }
  margin: 0 auto;
  padding: 20px 20px;
  align-items: center;
  justify-content: flex-start;
  display: flex;
`

const WebItem = styled.div`
  border: 1px solid ${style((theme) => theme.colors.ivory)};
  &:first-child {
    border-top-left-radius: ${style((theme) => theme.borderRadius.large)};
    border-top-right-radius: ${style((theme) => theme.borderRadius.large)};
  }
  &:not(:first-child):not(:last-child) {
    border-top: none;
  }
  &:last-child:not(:first-child) {
    border-top: none;
    border-bottom-left-radius: ${style((theme) => theme.borderRadius.large)};
    border-bottom-right-radius: ${style((theme) => theme.borderRadius.large)};
  }
  &:last-child:first-child {
    border-bottom-left-radius: ${style((theme) => theme.borderRadius.large)};
    border-bottom-right-radius: ${style((theme) => theme.borderRadius.large)};
  }
  margin: 0 auto;
  padding: 20px 20px;
  align-items: center;
  justify-content: flex-start;
  display: flex;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    width: 100%;
    &&& {
      border-bottom-left-radius: 0px;
      border-bottom-right-radius: 0px;
      border-top-left-radius: 0px;
      border-top-right-radius: 0px;
    }
  }
`
const StyledCheckboxInput = styled(CheckboxInput)`
  &&& {
    padding-bottom: 0px;
    & > input + label {
      &::before {
        border-radius: 50%;
      }
    }
  }
`
const Amount = styled.span`
  margin-top: 5px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  line-height: 22px;
  text-align: right;
  color: ${style((theme) => theme.colors.slate)};
  float: right;
  margin-left: auto;
  padding-left: 20px;
`

const InfoIconContainer = styled.div`
  display: block;
  text-align: left;
`
const AccountName = styled(Heading2)`
  font-size: 14px;
`

const AccountStatus = styled(Heading2)`
  font-size: 12px;
  font-family: AvenirNext-Regular;
  font-weight: normal;
  color: ${style((theme) => theme.colors.stone)};
`
const PendingStatus = styled(Heading2)`
  font-size: 12px;
  font-family: AvenirNext-Regular;
  font-weight: normal;
  color: ${style((theme) => theme.colors.sunrise)};
`
const StyledForm = styled.form`
  overflow: scroll;
  max-height: 400px;
`

const StyledWebForm = styled(StyledForm)`
  margin: 0 auto;
  max-width: 320px;
  height: auto;
  @media screen and (min-width: ${style((theme) => theme.breakpoints.sm)}px) {
    margin-bottom: 50px;
    max-height: 300px;
  }
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    max-height: 400px;
    max-width: 100%;
    margin: 0;
  }
`

const Footer = styled.div`
  font-family: AvenirNext-Regular;
  font-size: 12px;
  font-weight: normal;
  height: 16px;
  margin-bottom: 20px;
  color: ${style((theme) => theme.colors.stone)};
  @media screen and (min-width: ${style((theme) => theme.breakpoints.sm)}px) {
    margin-top: 30px;
  }
`
const ButtonContainer = styled.div`
  margin-top: 30px;
  @media screen and (min-width: ${style((theme) => theme.breakpoints.sm)}px) {
    margin: 0 auto;
    border-top: 1px solid ${style((theme) => theme.colors.ivory)};
  }
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    border-top: none;
    padding-left: 20px;
    padding-right: 20px;
  }
`
const ButtonContainerWV = styled.div`
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    margin-top: auto;
  }
`

const StyledWebMobileBody = styled(StyledBody)`
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    padding-left: 20px;
    padding-right: 20px;
  }
`

const StyledWVBody = styled(StyledBody)`
  margin: 10px auto 30px;
`
const EarlyChildrenContainer = (
  childrenAccounts,
  onChildSelection,
  children,
  isWebView,
) => {
  const formatStatus = (status) => {
    if (status === InvestmentAccountStatus.PendingClosure) {
      return <PendingStatus>Pending closure</PendingStatus>
    } else {
      return <AccountStatus>Account open</AccountStatus>
    }
  }
  const Item = isWebView ? WVItem : WebItem
  return childrenAccounts?.map((child) => (
    <Item key={child.id}>
      <StyledCheckboxInput
        checked={
          child.status === InvestmentAccountStatus.PendingClosure ||
          children.includes(child.id)
        }
        onChange={onChildSelection}
        name={child.id}
        label={''}
        disabled={child.status === InvestmentAccountStatus.PendingClosure}
        id={child.id}
      />
      <StyledEarlyAvatar />
      <InfoIconContainer>
        <AccountName>{child.beneficiary.firstName}</AccountName>
        {formatStatus(child.status)}
      </InfoIconContainer>
      <Amount>{USDFormatter.format(child.currentBalance.value)}</Amount>
    </Item>
  ))
}

const AccountSelection = ({ promptUser, analytics }: Props) => {
  const isWebView = window.location.pathname.startsWith('/wv/')
  const [selectedChildren, setChildSelected] = useState([])
  const [showConfirmCancelModal, setShowConfirmCancelModal] = useState(false)
  const [showLoadingScreen, setShowLoadingScreen] = useState(false)

  const { data: earlyAccountsData, loading: earlyAccountsDataLoading } =
    useQuery<QueryType>(earlyAccountsQuery, {
      fetchPolicy: 'no-cache',
    })
  const location = useLocation()
  const history = useHistory()
  const closureFormData = location.state

  const [closeEarlyAccounts] = useMutation<MutationType>(
    earlyAccountClosureMutation,
  )
  const onChildSelectChange = (event) => {
    selectedChildren.includes(event.target.id)
      ? setChildSelected(
          selectedChildren.filter((item) => item !== event.target.id),
        )
      : setChildSelected([...selectedChildren, event.target.id])
  }
  const hasBalance =
    earlyAccountsData?.investmentAccountsByUserId?.filter(
      (account) => account.currentBalance.value > 0,
    ).length > 0

  const messageWithBalance = `Your money will be transferred to your  ${earlyAccountsData?.user?.fundingSource?.name}
  ${earlyAccountsData?.user?.fundingSource?.last4} account. then we’ll automatically close your Early account(s).`

  const handleCloseAccounts = () => {
    analytics.track('Button Tapped', EarlyCloseConfirmationModalConfirmCTA)
    const url = isWebView ? `/wv${routes.early.success}` : routes.early.success
    setShowConfirmCancelModal(false)
    setShowLoadingScreen(true)
    closeEarlyAccounts({
      variables: {
        input: {
          investmentAccountIds: selectedChildren,
          reason: closureFormData.earlyClosureFormReasonKey,
        },
      },
    })
      .then(() => {
        setTimeout(() => history.push(url, { hasBalance }), 2000)
      })
      .catch(() => {
        setShowLoadingScreen(false)
        promptUser({
          title: 'Contact support',
          message:
            'We encountered an error processing your request. Please contact support to close your Early account(s).',
          actions: [{ type: ModalActionTypes.primary, label: 'Ok' }],
        })
      })
  }

  const handleSubmit = () => {
    analytics.track('Button Tapped', EarlyCloseAccountsCTATapped)
    analytics.track('Container Viewed', EarlyCloseConfirmationModal)
    setShowConfirmCancelModal(true)
  }
  const handleNevermindCTATapped = () => {
    analytics.track('Button Tapped', EarlyCloseConfirmationModalNevermindCTA)
    setShowConfirmCancelModal(false)
  }
  const backPressUrl = isWebView
    ? '/wv/close-account/early'
    : '/close-account/early'
  const handleBackPress = () => history.push(backPressUrl)
  const cancelUrl = isWebView
    ? '/wv/my-subscription'
    : '/settings/my-subscription'
  const handleCancel = () => history.push(cancelUrl)
  const { height } = useWindowDimensions()
  const openEarlyAccounts =
    earlyAccountsData?.investmentAccountsByUserId?.filter(
      (account) => account.status === InvestmentAccountStatus.Open,
    )
  const BodyText = `When you close your Early account(s), all funds will be
withdrawn and deposited into your 
${earlyAccountsData?.user?.fundingSource?.name} 
${earlyAccountsData?.user?.fundingSource?.last4} account.`

  return (
    <>
      {showLoadingScreen && (
        <LoadingScreen
          closureItems={['Processing...']}
          closureHeading="Requesting account closure"
          isVisible={showLoadingScreen}
        />
      )}
      {!showLoadingScreen && (
        <>
          {isWebView && (
            <>
              <FullscreenTakeover>
                <CloseAccountWrapper
                  isWebView={isWebView}
                  action={{
                    onClick: handleBackPress,
                    shouldShowCloseIcon: false,
                  }}
                  link={{ onClick: handleCancel, text: 'Cancel' }}
                  actionStripTitle="Close account"
                />

                <WVCard innerHeight={height}>
                  <StyledHeading>Just a heads up</StyledHeading>
                  {earlyAccountsData?.user?.fundingSource && (
                    <StyledWVBody>{BodyText}</StyledWVBody>
                  )}
                  <StyledForm>
                    <>
                      {EarlyChildrenContainer(
                        openEarlyAccounts,
                        onChildSelectChange,
                        selectedChildren,
                        isWebView,
                      )}
                    </>
                  </StyledForm>
                  <ButtonContainerWV>
                    <Footer>
                      Transfers may take up to 3-6 business days to settle.
                    </Footer>
                    <StyledCloseAccountButton
                      disabled={selectedChildren.length < 1}
                      onPress={handleSubmit}
                    >
                      Close account(s)
                    </StyledCloseAccountButton>
                  </ButtonContainerWV>
                </WVCard>
              </FullscreenTakeover>
            </>
          )}
          {!isWebView && (
            <>
              <ActionStrip
                maxWidth={800}
                onBack={handleBackPress}
                title="Close account"
                actions={[
                  {
                    icon: null,
                    id: 0,
                    text: 'Cancel',
                    handler: handleCancel,
                    actionText: 'cancel Early closure',
                  },
                ]}
              />

              <Card>
                <StyledHeading>Just a heads up</StyledHeading>
                {earlyAccountsData?.user?.fundingSource && (
                  <StyledWebMobileBody>{BodyText}</StyledWebMobileBody>
                )}

                <StyledWebForm>
                  <>
                    {EarlyChildrenContainer(
                      openEarlyAccounts,
                      onChildSelectChange,
                      selectedChildren,
                      isWebView,
                    )}
                  </>
                </StyledWebForm>
                <ButtonContainer>
                  <Footer>
                    Transfers may take up to 3-6 business days to settle.
                  </Footer>
                  <StyledCloseAccountButton
                    disabled={selectedChildren.length < 1}
                    onPress={handleSubmit}
                  >
                    Close account(s)
                  </StyledCloseAccountButton>
                </ButtonContainer>
              </Card>
            </>
          )}
        </>
      )}

      <ConfirmationModal
        title="Are you sure?"
        message={
          hasBalance
            ? messageWithBalance
            : 'Doing so will immediately close each of your Early accounts.'
        }
        actions={[
          {
            type: ModalActionTypes.secondary,
            label: 'Never mind',
            handler: handleNevermindCTATapped,
          },
          {
            type: ModalActionTypes.primary,
            label: "I'm sure",
            handler: handleCloseAccounts,
          },
        ]}
        isOpen={showConfirmCancelModal}
      />
    </>
  )
}

export default withAnalyticsClient(withPrompt()(AccountSelection))
